/**
 * Pre-fill settings — the DISTRICT view of Settings > My School.
 *
 * OVS is set by the district, with school-level overrides (Sheila,
 * 2026-11-04: *"OVS should be By Org, with location level overrides"*). This
 * card is where the district number lives: one value per meal part, per
 * mealtime, that every school ships unless it sets its own. It also names the
 * schools that do set their own, so an edit here is never mistaken for one
 * that reached everyone.
 *
 * The section's picker (`split-scope.tsx`) opens here by default and switches
 * to a school's own card (`PrefillSettingsCard`) for its overrides.
 */

import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { Pencil, Percent, Users } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { useComponentDeliveryRatesQuery, useDistrictSplitMatrixQuery } from '@/api/location-settings'
import type { AdminSchool } from '@/api/school-orders'
import { type Mealtime, MEALTIME_LABELS, sortMealtimes } from '@/lib/mealtime'
import { formatMealtimeSplitSummary } from '@/lib/school-orders/breakdown'
import { schoolsWithOverrides } from '@/lib/school-orders/district-overrides'

import { CardTitleRow } from './campus-scope'
import { DistrictDefaultsDialog } from './district-defaults-dialog'

export function DistrictPrefillCard({
  organizationId,
  organizationName,
  schools,
  scopeControl,
  onSelectSchool,
}: {
  organizationId: string
  organizationName: string
  /** The district's schools this admin administers; the first anchors the reads. */
  schools: AdminSchool[]
  scopeControl: ReactNode
  onSelectSchool: (locationId: string) => void
}) {
  const navigate = useNavigate()
  // District rows are read and written through any of its schools; the id
  // only tells the server which district is meant (see district-split.tsx).
  const anchorLocationId = schools[0]?.id ?? null
  const mealtimes = useMemo<Mealtime[]>(() => {
    const sorted = sortMealtimes(new Set(schools.flatMap((school) => school.servedMealtimes ?? [])))
    return sorted.length > 0 ? sorted : (['LUNCH'] as Mealtime[])
  }, [schools])
  const ratesQuery = useComponentDeliveryRatesQuery(anchorLocationId, 'ORGANIZATION')
  const matrixQuery = useDistrictSplitMatrixQuery(organizationId, mealtimes)
  const [editingMealtime, setEditingMealtime] = useState<Mealtime | null>(null)

  const titleRow = (
    <CardTitleRow scopeControl={scopeControl}>
      <CardTitle className="flex items-center gap-2 text-base">
        <Percent className="h-4 w-4" />
        Pre-fill settings
      </CardTitle>
    </CardTitleRow>
  )

  if (ratesQuery.isPending || matrixQuery.isPending) {
    return (
      <Card>
        <CardHeader className="pb-3">{titleRow}</CardHeader>
        <CardContent className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }
  const loadError = ratesQuery.error ?? matrixQuery.error
  if (loadError) {
    return (
      <Card>
        <CardHeader className="pb-3">{titleRow}</CardHeader>
        <CardContent className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
          Couldn&rsquo;t load the district&rsquo;s pre-fill settings. {loadError.message}
        </CardContent>
      </Card>
    )
  }

  const rates = ratesQuery.data?.rates ?? []
  const overriding = schoolsWithOverrides(matrixQuery.data)

  return (
    <Card>
      <CardHeader className="pb-3">{titleRow}</CardHeader>
      <CardContent className="flex flex-col gap-6">
        <section>
          <h3 className="text-sm font-medium">District component split</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            One split for every school in {organizationName}. A school can set its own number for any meal part — pick
            the school above.
          </p>

          <div className="mt-3 flex flex-col gap-2">
            {mealtimes.map((m) => (
              <div key={m} className="flex items-center justify-between gap-4 rounded-lg border p-4">
                <div className="min-w-0">
                  <h4 className="text-sm font-medium">{MEALTIME_LABELS[m]}</h4>
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {formatMealtimeSplitSummary(rates, m, { columns: [{ key: null }] })}
                  </p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setEditingMealtime(m)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit
                </Button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-medium">Schools with their own numbers</h3>
          {overriding.length === 0 ? (
            <p className="mt-0.5 text-sm text-muted-foreground">Every school follows the district.</p>
          ) : (
            <ul className="mt-2 flex flex-col divide-y rounded-lg border">
              {overriding.map((school) => (
                <li key={school.locationId} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{school.locationName}</p>
                    <p className="text-xs text-muted-foreground">
                      Own {school.mealtimes.map((m) => MEALTIME_LABELS[m].toLowerCase()).join(' and ')} numbers
                      {school.ageGroups ? ', with age-group values' : ''}
                    </p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => onSelectSchool(school.locationId)}>
                    Open
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
            onClick={() => navigate('/settings/my-school/district-split')}>
            <Users className="h-3.5 w-3.5" />
            Compare every school side by side
          </button>
        </section>
      </CardContent>

      {editingMealtime && anchorLocationId ? (
        <DistrictDefaultsDialog
          open
          onClose={() => setEditingMealtime(null)}
          organizationId={organizationId}
          organizationName={organizationName}
          anchorLocationId={anchorLocationId}
          mealtime={editingMealtime}
        />
      ) : null}
    </Card>
  )
}
